import React from 'react';
import { DataTableColumn, FilterRule, FilterOperator, FilterOption } from '../../../types/grid';

export interface FilterSearchBarProps<T extends Record<string, any> = any> {
  columns: DataTableColumn<T>[];
  filterRules: FilterRule[];
  onFilterRulesChange: (rules: FilterRule[]) => void;
  searchValue: string;
  onSearchChange: (search: string) => void;
  placeholder?: string;
  className?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

export interface OperatorDef {
  op: FilterOperator;
  symbol: string;
  label: string;
  value?: string;
}

export type SearchBarStage = 'column' | 'operator' | 'value' | 'boolean' | 'enum';

export function normalizeDateValue(raw: string): string {
  const v = raw.trim();
  if (!v) return v;
  const lower = v.toLowerCase();
  const now = new Date();
  if (lower === 'today') {
    return now.toISOString().slice(0, 10);
  }
  if (lower === 'yesterday') {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    return y.toISOString().slice(0, 10);
  }
  if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(v)) {
    const parts = v.split('/');
    return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
  }
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(v)) {
    const parts = v.split('/');
    if (parseInt(parts[0], 10) > 12) {
      return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }
    return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
  }
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(v)) {
    const parts = v.split('-');
    return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
  }
  return v;
}

export function getDatePresets() {
  const now = new Date();
  const format = (d: Date) => d.toISOString().slice(0, 10);

  const today = format(now);

  const yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  const yesterday = format(yest);

  const d7 = new Date(now);
  d7.setDate(d7.getDate() - 7);
  const sevenDaysAgo = format(d7);

  const d30 = new Date(now);
  d30.setDate(d30.getDate() - 30);
  const thirtyDaysAgo = format(d30);

  const startOfYear = `${now.getFullYear()}-01-01`;

  return [
    { label: 'Today', value: today },
    { label: 'Yesterday', value: yesterday },
    { label: '7 days ago', value: sevenDaysAgo },
    { label: '30 days ago', value: thirtyDaysAgo },
    { label: 'Start of year', value: startOfYear },
  ];
}

export const isBooleanColumn = <T extends Record<string, any>>(col: DataTableColumn<T>): boolean => {
  return col.dataType === 'boolean';
};

export const isEnumColumn = <T extends Record<string, any>>(col: DataTableColumn<T>): boolean => {
  return (
    col.dataType === 'enum' ||
    col.dataType === 'status' ||
    col.id === 'status' ||
    (Boolean(col.options) && col.options!.length > 0)
  );
};

export const getColumnOptions = <T extends Record<string, any>>(col: DataTableColumn<T>): FilterOption[] => {
  if (col.options && col.options.length > 0) {
    return col.options;
  }
  if (col.id === 'status' || col.dataType === 'status') {
    return [
      { value: 'OK', label: 'OK', color: 'emerald' },
      { value: 'SPAM', label: 'SPAM', color: 'rose' },
    ];
  }
  return [];
};

export const getColumnName = <T extends Record<string, any>>(col: DataTableColumn<T>): string => {
  if (typeof col.header === 'string') return col.header;
  return col.id.replace(/^data_/, '').replace(/_/g, ' ');
};

export const getOperatorSymbol = (rule: FilterRule): string => {
  switch (rule.operator) {
    case 'equals':
      return '=';
    case 'not_equals':
      return '!=';
    case 'greater_than':
      return '>';
    case 'less_than':
      return '<';
    case 'contains':
      return 'contains';
    case 'not_contains':
      return 'not contains';
    case 'starts_with':
      return '^';
    case 'boolean':
      return rule.value === 'true' ? 'is true' : 'is false';
    case 'is_on':
      return '=';
    case 'is_before':
      return '<';
    case 'is_after':
      return '>';
    case 'is_empty':
      return 'is empty';
    case 'is_not_empty':
      return 'is not empty';
    default:
      return '=';
  }
};
